import { Fragment, useEffect, useRef, useState } from 'react'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { Calendar } from '@progress/kendo-react-dateinputs'
import styles from './DateInput.module.scss'
import notificationStyles from '../NotificationManager/NotificationManager.module.scss'
import { Slide } from '@progress/kendo-react-animation'
import { TextField } from '@material-ui/core'
// import useCallback from 'react'
import React from 'react'
import clsx from 'clsx'
import ClickAwayListener from 'react-click-away-listener'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
// import _ from 'lodash'

const isValidShortDate = (value: string) => {
    let groups = /^(\d{2})\/(\d{2})\/(\d{2})__$/.exec(value)
    if (!groups || +groups[1] > 12 || +groups[2] > 31) return false
    return !isNaN(parseShortDate(value).getTime())
}

const parseShortDate = (value: string) => {
    let groups = /^(\d{2})\/(\d{2})\/(\d{2})__$/.exec(value)!
    return new Date(2000 + parseInt(groups[3]), +groups[1] - 1, +groups[2])
}

export enum DATE_INPUT_POPUP_TYPES {
    POPOVER = 'POPOVER',
    MODAL = 'MODAL',
}

interface DatePickerProps {
    onChange?(newDate: Date | null): void
    onFocus?(): void
    onValidityChange?(validity: boolean): void
    value?: Date | null | string
    validityText?: string
    minDate?: Date
    maxDate?: Date
    style?: React.CSSProperties
    format?: 'long' | 'short'
    required?: boolean
    positionRight?: boolean
    hideErrorMessage?: boolean
    popupType?: DATE_INPUT_POPUP_TYPES
}
export const DatePicker = (props: DatePickerProps) => {
    const {
        onValidityChange,
        onFocus,
        validityText,
        minDate,
        maxDate = new Date(2999, 11, 30),
        format = 'long',
        //value,
        //style,
        required,
        positionRight,
        hideErrorMessage,
        popupType,
    } = props
    const [date, setDate] = useState<Date | null>()
    const [lastValue, setLastValue] = useState<Date | null | undefined>(undefined)
    const valid = useRef(true)
    const setValid = (b: boolean) => {
        valid.current = b
    }
    // const [valid, setValid] = useState(true)
    const [calendarVisible, setCalendarVisible] = useState(false)
    const [focused, setFocused] = useState(false)
    const [hadValidDate, setHadValidDate] = useState(true)
    const containerRef = useRef<any>()

    let isValid = valid.current && !validityText

    const onChange = (date: Date | null) => {
        if (!date || isValidDate(date)) {
            props.onChange && props.onChange(date)
            setLastValue(date)
        }
    }

    const handleKeyPressed = React.useCallback((event: any) => {
        if (event.keyCode === 13) {
            event.target.blur()
        }
    }, [])

    const WrappedTextField = React.useCallback(
        (props2) => {
            let isValid = valid.current && !props.validityText

            let validityProps = isValid ? {} : { error: true, helperText: props.validityText }

            let newProps = { ...props2 }
            if (!newProps.helperText) {
                newProps.helperText = validityProps.helperText
                newProps.error = validityProps.error
            }
            if (!newProps.value || !hadValidDate) {
                delete newProps.error
                delete newProps.helperText
            }
            return (
                <TextField
                    key='dateInputField'
                    inputProps={{ key: 'innerDateInput' }}
                    InputProps={{ key: 'innerDateInputJsx' }}
                    onKeyDown={handleKeyPressed}
                    {...newProps}
                    ref={newProps.inputRef}
                />
            )
        },
        [props.validityText, hadValidDate]
    )

    useEffect(() => {
        if (props.value !== lastValue) {
            if (props.value) {
                let possibleNewDate = new Date(props.value)
                // if valid Date
                if (!isNaN(possibleNewDate.getTime())) {
                    // if lastValue was a date only set if they're different
                    // could be late getting update that is old value
                    if (lastValue) {
                        if (lastValue.getTime() !== possibleNewDate.getTime()) {
                            setDate(possibleNewDate)
                            updateValidity(possibleNewDate)
                        }
                    } else {
                        setDate(possibleNewDate)
                        updateValidity(possibleNewDate)
                    }
                    setLastValue(possibleNewDate)
                }
            } else {
                setDate(null)
                setLastValue(undefined)
            }
        }
    }, [props.value])

    const isValidDate = (date: Date) => {
        return (
            !isNaN(date.getTime()) &&
            (!minDate || new Date(minDate.setHours(0, 0, 0, 0)) <= date) &&
            (!maxDate || maxDate > date)
        )
    }

    const updateValidity = (date: Date | null) => {
        let dateValid = (!required && date === null) || (!!date && isValidDate(date))
        if (date && !isNaN(date.getTime()) && !hadValidDate) {
            setHadValidDate(true)
        }
        if (valid.current !== dateValid) {
            setValid(dateValid)
            onValidityChange && onValidityChange(dateValid)
        }
    }

    const onBlur = (date: MaterialUiPickersDate, val: string) => {
        setFocused(false)
        // if date is blanked out and the lastValue wasn't empty
        if (!date && lastValue !== null) {
            onChange(null)
        } else if (date && lastValue) {
            // if date and lastValue aren't the same date, updateDate
            if (isValidDate(date) && date.getTime() !== lastValue.getTime()) {
                onChange(date)
            }

            // if it's a short date we'll just change it
            if (isValidShortDate(val)) {
                let newDate = parseShortDate(val)
                onChange(newDate)
                setDate(newDate)
                updateValidity(newDate)
            }
        }
        // if there was no last value update if valid
        else if (date) {
            if (isValidDate(date)) {
                onChange(date)
            }
            if (isValidShortDate(val)) {
                let newDate = parseShortDate(val)
                onChange(newDate)
                setDate(newDate)
                updateValidity(newDate)
            }
        }
    }

    return (
        <>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <>
                    <div
                        ref={containerRef}
                        style={{ zIndex: 0 }}
                        className={clsx([
                            !isValid && hadValidDate && 'a-invalid',
                            styles.dateInput,
                        ])}
                    >
                        <KeyboardDatePicker
                            key={'datePicker'}
                            clearable
                            value={date || null}
                            placeholder={
                                focused ? (format === 'long' ? '__/__/____' : '__/__/__') : ''
                            }
                            onChange={(date, value) => {
                                setDate(date)
                                if (value && value.length === 10 && value.charAt(9) !== '_')
                                    setHadValidDate(true)
                                updateValidity(date)
                                onBlur(date, value!)
                            }}
                            TextFieldComponent={WrappedTextField}
                            keyboardIcon={<div className='k-icon k-i-calendar'></div>}
                            DialogProps={{
                                className: notificationStyles.maxZIndex,
                            }}
                            minDate={minDate}
                            minDateMessage={hideErrorMessage ? '' : 'Date cannot be in the past'}
                            maxDate={maxDate}
                            maxDateMessage={hideErrorMessage ? '' : 'Too far in the future'}
                            invalidDateMessage={
                                hadValidDate || !hideErrorMessage ? 'Invalid Date' : ''
                            }
                            format={format === 'long' ? 'MM/dd/yyyy' : 'MM/dd/yy'}
                            onOpen={() => setCalendarVisible(!calendarVisible)}
                            onClose={() => setCalendarVisible(!calendarVisible)}
                            onBlur={(e) => onBlur(date as Date, e.target.value)}
                            open={
                                (popupType === DATE_INPUT_POPUP_TYPES.MODAL && calendarVisible) ||
                                false
                            }
                            onFocus={(e) => {
                                setFocused(true)
                                onFocus && onFocus()
                                e.target.select()
                            }}
                        />
                    </div>
                    {popupType !== DATE_INPUT_POPUP_TYPES.MODAL && (
                        <div
                            style={
                                positionRight
                                    ? { position: 'absolute', right: 0 }
                                    : { position: 'absolute' }
                            }
                        >
                            <Slide>
                                {calendarVisible && (
                                    <ClickAwayListener
                                        onClickAway={() => setCalendarVisible(false)}
                                    >
                                        <div>
                                            <Calendar
                                                bottomView={'month'}
                                                topView={'month'}
                                                onChange={(e) => {
                                                    setDate(e.value)
                                                    onChange(e.value)
                                                    updateValidity(e.value)
                                                    setCalendarVisible(false)
                                                }}
                                                value={
                                                    date && !isNaN(date.getTime())
                                                        ? date
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    </ClickAwayListener>
                                )}
                            </Slide>
                        </div>
                    )}
                </>
            </MuiPickersUtilsProvider>
        </>
    )
}
